// Render a discoveryFeatures payload as a markdown spec file. Pure
// function — no DOM, no network. Used by the command palette's
// "Download features as spec" action.
//
// Format prioritizes "what would I hand to a coding agent?" — one
// section per feature, statement-id refs preserved, MVP first.

import type { DiscoveryFeaturesPayload, DiscoveryFeature } from '../hooks/useSession';
import type { ParsedFlow } from './flowModel';

export interface SpecMarkdownOptions {
  sessionTitle?: string;
  sessionId?: string;
}

export function renderFeatureSpecMarkdown(
  payload: DiscoveryFeaturesPayload,
  flow: ParsedFlow,
  opts: SpecMarkdownOptions = {},
): string {
  const lines: string[] = [];
  const title = opts.sessionTitle?.trim() || 'Discovery Features Spec';
  lines.push(`# ${title}`);
  lines.push('');
  if (opts.sessionId) lines.push(`**Session:** \`${opts.sessionId}\`  `);
  if (payload.extractedAt) {
    lines.push(`**Extracted:** ${payload.extractedAt}  `);
  }
  lines.push(`**Statements covered:** ${payload.statementCountAtExtraction ?? '—'}  `);
  lines.push(`**Features:** ${payload.features.length}`);
  lines.push('');

  if (payload.warnings.length) {
    lines.push('> ⚠ Extraction produced warnings:');
    for (const w of payload.warnings) lines.push(`> - ${w}`);
    lines.push('');
  }

  if (payload.features.length === 0) {
    lines.push('_No features extracted yet._');
    return lines.join('\n');
  }

  // Stable ordering: MVP before later, then by rootStatementId so the
  // happy path reads top-to-bottom in flow order.
  const sorted = [...payload.features].sort((a, b) => {
    if (a.priority !== b.priority) return a.priority === 'mvp' ? -1 : 1;
    return a.rootStatementId - b.rootStatementId;
  });

  const stmtById = new Map(flow.statements.map((s) => [s.id, s]));

  let mvpHeaderEmitted = false;
  let laterHeaderEmitted = false;
  for (const f of sorted) {
    if (f.priority === 'mvp' && !mvpHeaderEmitted) {
      lines.push('---');
      lines.push('');
      lines.push('## MVP');
      lines.push('');
      mvpHeaderEmitted = true;
    }
    if (f.priority === 'later' && !laterHeaderEmitted) {
      lines.push('---');
      lines.push('');
      lines.push('## Later');
      lines.push('');
      laterHeaderEmitted = true;
    }

    lines.push(`### ${f.name}`);
    lines.push('');
    lines.push(f.description);
    lines.push('');
    lines.push(`- **Trigger:** ${triggerLabel(f.trigger)}`);
    if (f.boundedContext) lines.push(`- **Bounded context:** \`${f.boundedContext}\``);
    if (f.touchpoints.length) lines.push(`- **Touchpoints:** ${f.touchpoints.map((t) => `\`${t}\``).join(', ')}`);
    if (f.targets.length) lines.push(`- **Aggregates:** ${f.targets.map((t) => `\`&${t}\``).join(', ')}`);
    lines.push(`- **Statements:** ${f.statementIds.map((id) => `\`[${id}]\``).join(', ')}`);
    lines.push('');

    // Statement detail block — gives a coding agent the actual
    // grammar lines to implement against.
    lines.push('```');
    for (const id of f.statementIds) {
      const s = stmtById.get(id);
      if (s) lines.push(s.source);
    }
    lines.push('```');
    lines.push('');
  }

  return lines.join('\n');
}

function triggerLabel(t: DiscoveryFeature['trigger']): string {
  if (t === 'user-action') return 'user action (human-initiated)';
  if (t === 'system-reaction') return 'system reaction (event-driven)';
  if (t === 'external-system') return 'external system (webhook / push)';
  return t;
}

// Trigger a browser download of the rendered markdown.
export function downloadFeatureSpec(
  payload: DiscoveryFeaturesPayload,
  flow: ParsedFlow,
  opts: SpecMarkdownOptions = {},
): void {
  const markdown = renderFeatureSpecMarkdown(payload, flow, opts);
  const slug = (opts.sessionTitle || 'features-spec')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'features-spec';
  const date = new Date().toISOString().slice(0, 10);
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${slug}-${date}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
